import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import IsqmNoTargetDateRow from "@/components/IsqmNoTargetDateRow";   // Silent defaults · A-29
import React, { useEffect, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import { fmtDate } from "@/lib/dt";
import { Link } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Shield, ShieldCheck, ChevronRight, Info,
  BarChart3, ClipboardList, Download,
} from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { engagementName } from "@/lib/engagementLabel";   // Batch 15 · N5 (F-119)
import { RegistersSection } from "./isqm/IsqmRegisters";            // split step 5b: moved out, unchanged
import { AccessManageButton, IsqmVerifyBundleButton } from "./isqm/IsqmAccess";
export { ISQMFirmForm, ISQMEngForm } from "./isqm/IsqmFormPage";    // App.js imports them from here

export default function ISQM() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { current } = useEngagement();
  const [access, setAccess] = useState(null);
  const [firm, setFirm] = useState(null);
  const [eng, setEng] = useState(null);
  const [firmCompletion, setFirmCompletion] = useState({});
  const [engCompletion, setEngCompletion] = useState({});
  const [tab, setTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const isPartner = user?.role === "Partner" || user?.role === "Admin";

  const loadAccess = async () => {
    try {
      const r = await api.get("/isqm/my-access", { _silent: true });
      setAccess(r.data);
    } catch { setAccess({ is_partner: false, firm_isqm1: false, engagement_isqm2: [] }); }
  };
  const loadFirm = async () => {
    try {
      const [f, c] = await Promise.all([
        api.get("/isqm/firm", { _silent: true }),
        api.get("/isqm/completion/firm", { _silent: true }),
      ]);
      setFirm(f.data); setFirmCompletion(c.data.per_form || {});
    } catch { setFirm(null); }
  };
  const loadEng = async (eid) => {
    if (!eid) { setEng(null); return; }
    try {
      const [e, c] = await Promise.all([
        api.get(`/isqm/engagement/${eid}`, { _silent: true }),
        api.get(`/isqm/completion/engagement/${eid}`, { _silent: true }),
      ]);
      setEng(e.data); setEngCompletion(c.data.per_form || {});
    } catch { setEng(null); }
  };

  useEffect(() => { (async () => { setLoading(true); await loadAccess(); setLoading(false); })(); }, []);
  useEffect(() => {
    if (!access) return;
    if (access.firm_isqm1) loadFirm();
    if (current?.id && access.engagement_isqm2?.includes(current.id)) loadEng(current.id);
    // eslint-disable-next-line
  }, [access?.firm_isqm1, current?.id]);

  if (loading) return <div className="p-8 text-sm text-muted-foreground">{t("isqmPage.loading")}</div>;
  if (!access || (!access.firm_isqm1 && (access.engagement_isqm2 || []).length === 0)) {
    return (
      <Card className="border-red-200 bg-red-50/60 dark:border-red-900 dark:bg-red-950/40">
        <CardContent className="p-8 text-center text-sm text-red-800 dark:text-red-200">
          {t("isqmPage.accessDenied")}
        </CardContent>
      </Card>
    );
  }
  const canSeeEng = current?.id && access.engagement_isqm2?.includes(current.id);
  const engRequiresEqc = current?.requires_eqc;

  return (
    <div className="space-y-6" data-testid="isqm-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            {t("isqmPage.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("isqmPage.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <IsqmVerifyBundleButton />
          {isPartner && <AccessManageButton />}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="isqm-tabs">
          {access.firm_isqm1 && (
            <TabsTrigger value="dashboard" data-testid="isqm-tab-dashboard">
              <BarChart3 className="me-1 h-3.5 w-3.5" /> {t("isqmPage.tabDashboard")}
            </TabsTrigger>
          )}
          {access.firm_isqm1 && (
            <TabsTrigger value="firm" data-testid="isqm-tab-firm">
              <Shield className="me-1 h-3.5 w-3.5" /> {t("isqmPage.tabFirm")}
            </TabsTrigger>
          )}
          {access.firm_isqm1 && (
            <TabsTrigger value="registers" data-testid="isqm-tab-registers">
              <ClipboardList className="me-1 h-3.5 w-3.5" /> {t("isqmPage.tabRegisters")}
            </TabsTrigger>
          )}
          {canSeeEng && (
            <TabsTrigger value="eng" data-testid="isqm-tab-eng">
              <ChevronRight className="me-1 h-3.5 w-3.5" /> {t("isqmPage.tabEng").replace("{client}", (current && engagementName(current, t)) || "")}
            </TabsTrigger>
          )}
        </TabsList>

        {access.firm_isqm1 && (
          <TabsContent value="dashboard" className="space-y-4">
            <DashboardSection />
          </TabsContent>
        )}
        {access.firm_isqm1 && (
          <TabsContent value="firm" className="space-y-4">
            <FirmSection firm={firm} completion={firmCompletion} onChanged={loadFirm} />
          </TabsContent>
        )}
        {access.firm_isqm1 && (
          <TabsContent value="registers" className="space-y-4">
            <RegistersSection />
          </TabsContent>
        )}
        {canSeeEng && (
          <TabsContent value="eng" className="space-y-4">
            <EngagementSection eng={eng} completion={engCompletion} engId={current?.id}
                                 onChanged={() => loadEng(current?.id)} />
          </TabsContent>
        )}
      </Tabs>

      {isPartner && current?.id && !engRequiresEqc && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
             data-testid="isqm-eqc-hint">
          <Info className="me-1 inline h-3 w-3" />
          {t("isqmPage.eqcHintPre")} <b>{current && engagementName(current, t)}</b> {t("isqmPage.eqcHintPost")}
        </div>
      )}
    </div>
  );
}


// ======================================================================
// Completion badge helper — mirrors Planning/Risk/Completion badge look
// ======================================================================
function CompletionBadge({ pct, testid }) {
  if (pct == null) return null;
  const tone = pct >= 100 ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300"
              : pct >= 50 ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold tabular-nums ${tone}`}
          data-testid={testid}>
      {pct}%
    </span>
  );
}


// ======================================================================
// Dashboard — Part D (landing view, at-a-glance SOQM status)
// ======================================================================
function DashboardSection() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const loadDashboard = useEffectEvent(() => {   // once per mount (t is read for the error text only)
    api.get("/isqm/dashboard").then((r) => setData(r.data))
      .catch((e) => toast.error(toErrMsg(e, t("isqmPage.dashLoadFailed"))));
  });
  useEffect(() => { loadDashboard(); }, [loadDashboard]);
  if (!data) return <div className="p-6 text-sm text-muted-foreground">{t("isqmPage.dashLoading")}</div>;

  const d = data.deficiencies || {};
  const rem = data.remediations || {};
  const ann = data.annual_evaluation || {};
  const comp = data.complaints || {};

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="isqm-dashboard">
      <Card data-testid="isqm-dash-deficiencies">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">{t("isqmPage.dashDeficienciesTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-red-700 dark:text-red-400">{t("isqmPage.dashOpen")}</span>
              <span className="text-2xl font-semibold tabular-nums">{d.open || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-amber-700 dark:text-amber-400">{t("isqmPage.dashPendingEff")}</span>
              <span className="text-lg font-semibold tabular-nums">{d.pending_effectiveness_review || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-emerald-700 dark:text-emerald-400">{t("isqmPage.dashRemediated")}</span>
              <span className="text-lg font-semibold tabular-nums">{d.remediated || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="isqm-dash-remediations">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">{t("isqmPage.dashRemediationsTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="text-red-700 dark:text-red-400">{t("isqmPage.dashOverdue")}</span>
              <span className="text-2xl font-semibold tabular-nums" data-testid="isqm-dash-overdue">{rem.overdue?.count || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-amber-700 dark:text-amber-400">{t("isqmPage.dashDueSoon")}</span>
              <span className="text-lg font-semibold tabular-nums">{rem.due_soon?.count || 0}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span>{t("isqmPage.dashOnTrack")}</span>
              <span className="text-lg font-semibold tabular-nums">{rem.on_track?.count || 0}</span>
            </div>
            <IsqmNoTargetDateRow rem={rem} t={t} />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>{t("isqmPage.dashCompleted")}</span>
              <span className="tabular-nums">{rem.completed || 0}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="isqm-dash-annual">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">{t("isqmPage.dashAnnualTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <div className="text-lg font-semibold" data-testid="isqm-dash-conclusion">
            {ann.conclusion || t("isqmPage.dashNotConcluded")}
          </div>
          <div className="text-[11px] text-muted-foreground">
            {ann.last_saved_at
              ? t("isqmPage.dashLastSaved").replace("{date}", fmtDate(ann.last_saved_at))
              : t("isqmPage.dashNoSave")}
          </div>
          <Link to="/isqm/firm/azex_512a" className="text-[11px] text-emerald-600 hover:underline">
            {t("isqmPage.dashOpen512a")}
          </Link>
        </CardContent>
      </Card>

      <Card data-testid="isqm-dash-complaints">
        <CardHeader className="pb-2">
          <CardTitle className="text-xs uppercase tracking-wider text-muted-foreground">{t("isqmPage.dashComplaintsTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          <div className="flex items-center justify-between text-sm">
            <span className="text-red-700 dark:text-red-400">{t("isqmPage.dashOpen")}</span>
            <span className="text-2xl font-semibold tabular-nums">{comp.open || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-amber-700 dark:text-amber-400">{t("isqmPage.dashUnderInvestigation")}</span>
            <span className="text-lg font-semibold tabular-nums">{comp.under_investigation || 0}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


function FirmSection({ firm, completion, onChanged }) {
  const colW = useResizableColumns({ storageKey: "isqm-firm-forms", defaults: [240, 240, 240, 130, 100], mins: [130, 130, 130, 90, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const exportPdf = async (formId) => {
    try {
      const body = formId ? { form_id: formId } : { all: true };
      const r = await api.post("/isqm/export/firm", body, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `isqm1_${formId || "all"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(t("isqmPage.toastExportedSha").replace("{sha}", r.headers["x-sha256"]?.slice(0, 12) || ""));
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastExportFailed"))); }
  };
  if (!firm) return <div className="text-sm text-muted-foreground">{t("isqmPage.firmLoading")}</div>;
  const forms = asArray(firm.forms).sort((a, b) => a.number.localeCompare(b.number));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("isqmPage.firmTitle")}</CardTitle>
        <Button size="sm" variant="outline" onClick={() => exportPdf(null)} data-testid="isqm-firm-export-all">
          <Download className="me-1 h-3.5 w-3.5" /> {t("isqmPage.firmExportAll")}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <ResizableTable cols={colW} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
            <tr>
              <ResizableTh index={0} className="px-4 py-2 text-start">{t("isqmPage.firmColRef")}</ResizableTh>
              <ResizableTh index={1} className="px-4 py-2 text-start">{t("isqmPage.firmColForm")}</ResizableTh>
              <ResizableTh index={2} className="px-4 py-2 text-start">{t("isqmPage.firmColStandard")}</ResizableTh>
              <ResizableTh index={3} className="px-4 py-2 text-end w-32">{t("isqmPage.firmColCompletion")}</ResizableTh>
              <ResizableTh index={4} className="px-4 py-2 text-end w-40"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {forms.map((f) => (
              <tr key={f.id} className="border-b" data-testid={`isqm-firm-row-${f.id}`}>
                <td className="px-4 py-2 font-mono text-xs tabular-nums">{f.number}</td>
                <td className="px-4 py-2 font-medium">{f.name}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{f.isa}</td>
                <td className="px-4 py-2 text-end">
                  <CompletionBadge pct={completion?.[f.id]?.pct} testid={`isqm-firm-pct-${f.id}`} />
                </td>
                <td className="px-4 py-2 text-end">
                  <Link to={`/isqm/firm/${f.id}`}
                        className="text-emerald-600 hover:underline me-2"
                        data-testid={`isqm-firm-open-${f.id}`}>
                    {t("isqmPage.firmOpen")}
                  </Link>
                  <button onClick={() => exportPdf(f.id)}
                          className="text-xs text-slate-600 hover:underline"
                          data-testid={`isqm-firm-export-${f.id}`}>
                    {t("isqmPage.firmPdf")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </CardContent>
    </Card>
  );
}


function EngagementSection({ eng, completion, engId, onChanged }) {
  const colW2 = useResizableColumns({ storageKey: "isqm-engagement-forms", defaults: [240, 240, 240, 130, 100], mins: [130, 130, 130, 90, 60] });   // Step 11d · add-on
  const { t } = useI18n();
  const exportPdf = async (formId) => {
    try {
      const body = formId ? { form_id: formId } : { all: true };
      const r = await api.post(`/isqm/export/engagement/${engId}`, body, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `isqm2_${formId || "all"}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t("isqmPage.toastExportedSha").replace("{sha}", r.headers["x-sha256"]?.slice(0, 12) || ""));
    } catch (e) { toast.error(toErrMsg(e, t("isqmPage.toastExportFailed"))); }
  };
  if (!eng) return <div className="text-sm text-muted-foreground">{t("isqmPage.engLoading")}</div>;
  const forms = asArray(eng.forms).sort((a, b) => a.number.localeCompare(b.number));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t("isqmPage.engTitle")}</CardTitle>
        <Button size="sm" variant="outline" onClick={() => exportPdf(null)} data-testid="isqm-eng-export-all">
          <Download className="me-1 h-3.5 w-3.5" /> {t("isqmPage.firmExportAll")}
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <ResizableTable cols={colW2} wrap className="w-full text-sm">
          <thead className="border-b bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground dark:bg-slate-900/60">
            <tr>
              <ResizableTh index={0} className="px-4 py-2 text-start">{t("isqmPage.firmColRef")}</ResizableTh>
              <ResizableTh index={1} className="px-4 py-2 text-start">{t("isqmPage.firmColForm")}</ResizableTh>
              <ResizableTh index={2} className="px-4 py-2 text-start">{t("isqmPage.firmColStandard")}</ResizableTh>
              <ResizableTh index={3} className="px-4 py-2 text-end w-32">{t("isqmPage.firmColCompletion")}</ResizableTh>
              <ResizableTh index={4} className="px-4 py-2 text-end w-40"></ResizableTh>
            </tr>
          </thead>
          <tbody>
            {forms.map((f) => (
              <tr key={f.id} className="border-b" data-testid={`isqm-eng-row-${f.id}`}>
                <td className="px-4 py-2 font-mono text-xs tabular-nums">{f.number}</td>
                <td className="px-4 py-2 font-medium">{f.name}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground">{f.isa}</td>
                <td className="px-4 py-2 text-end">
                  <CompletionBadge pct={completion?.[f.id]?.pct} testid={`isqm-eng-pct-${f.id}`} />
                </td>
                <td className="px-4 py-2 text-end">
                  <Link to={`/isqm/engagement/${engId}/form/${f.id}`}
                        className="text-emerald-600 hover:underline me-2"
                        data-testid={`isqm-eng-open-${f.id}`}>
                    {t("isqmPage.firmOpen")}
                  </Link>
                  <button onClick={() => exportPdf(f.id)}
                          className="text-xs text-slate-600 hover:underline"
                          data-testid={`isqm-eng-export-${f.id}`}>
                    {t("isqmPage.firmPdf")}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </ResizableTable>
      </CardContent>
    </Card>
  );
}
